import React, { useEffect, useState, useRef, cloneElement } from 'react';
import { ShieldIcon, UsersIcon, BriefcaseIcon, CheckIcon, ClockIcon, ArrowRightIcon, StarIcon, UsersRoundIcon, BookOpenIcon, TrendingUpIcon, AwardIcon, BarChartIcon } from 'lucide-react';
export const IndustriesSection = () => {
  const [activeTab, setActiveTab] = useState('compliance');
  const [isAnimating, setIsAnimating] = useState(false);
  const sectionRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-fade-in');
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const handleTabChange = tab => {
    if (tab !== activeTab) {
      setIsAnimating(true);
      setTimeout(() => {
        setActiveTab(tab);
        setTimeout(() => {
          setIsAnimating(false);
        }, 50);
      }, 300);
    }
  };
  const tabs = {
    compliance: {
      title: 'Compliance',
      icon: <ShieldIcon size={20} className="text-teal" data-id="element-3181" />,
      courses: [{
        name: 'Food Safety',
        duration: '2 hours'
      }, {
        name: 'GDPR',
        duration: '1.5 hours'
      }, {
        name: 'Health & Safety',
        duration: '3 hours'
      }, {
        name: 'Fire Safety',
        duration: '1 hour'
      }, {
        name: 'Manual Handling',
        duration: '2 hours'
      }, {
        name: 'Alcohol Serving',
        duration: '1.5 hours'
      }, {
        name: 'Anti-Bribery',
        duration: '1 hour'
      }, {
        name: 'Legionella',
        duration: '1.5 hours'
      }],
      featuredCourses: [{
        title: 'Food Safety Level 2',
        image: 'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
        badge: 'Most Popular',
        description: 'Comprehensive food safety training for all food handlers. Meets EU and UK standards.',
        students: '12,450+',
        duration: '2 hours',
        icon: <BookOpenIcon data-id="element-3182" />,
        color: 'bg-amber-500'
      }, {
        title: 'Health & Safety Essentials',
        image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
        badge: 'Essential',
        description: 'Core health and safety principles for workplace compliance and risk management.',
        students: '8,320+',
        duration: '3 hours',
        icon: <ShieldIcon data-id="element-3183" />,
        color: 'bg-blue-500'
      }, {
        title: 'GDPR Fundamentals',
        image: 'https://images.unsplash.com/photo-1563906267088-b029e7101114?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
        badge: 'Updated',
        description: 'Stay compliant with the latest data protection regulations and best practices.',
        students: '7,890+',
        duration: '1.5 hours',
        icon: <CheckIcon data-id="element-3184" />,
        color: 'bg-green-500'
      }],
      popularCategories: [{
        name: 'Food & Beverage',
        count: '24 courses'
      }, {
        name: 'Workplace Safety',
        count: '18 courses'
      }, {
        name: 'Data Protection',
        count: '12 courses'
      }, {
        name: 'Health Regulations',
        count: '15 courses'
      }],
      path: '/compliance'
    },
    workplace: {
      title: 'Workplace & HR',
      icon: <UsersIcon size={20} className="text-teal" data-id="element-3185" />,
      courses: [{
        name: 'HR Essentials',
        duration: '3 hours'
      }, {
        name: 'Leadership',
        duration: '4 hours'
      }, {
        name: 'Diversity',
        duration: '2 hours'
      }, {
        name: 'Bullying & Harassment',
        duration: '2 hours'
      }, {
        name: 'Sexual Harassment Prevention',
        duration: '2 hours'
      }, {
        name: 'Staff Skills',
        duration: '3 hours'
      }],
      featuredCourses: [{
        title: 'Leadership Excellence',
        image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
        badge: 'Top Rated',
        description: 'Develop essential leadership skills to inspire and guide your team effectively.',
        students: '9,120+',
        duration: '4 hours',
        icon: <UsersIcon data-id="element-3186" />,
        color: 'bg-indigo-500'
      }, {
        title: 'Workplace Diversity & Inclusion',
        image: 'https://images.unsplash.com/photo-1573497620053-ea5300f94f21?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
        badge: 'Essential',
        description: 'Create an inclusive workplace culture that values diversity and promotes equality.',
        students: '6,780+',
        duration: '2 hours',
        icon: <UsersRoundIcon data-id="element-3187" />,
        color: 'bg-purple-500'
      }, {
        title: 'HR Compliance Fundamentals',
        image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
        badge: 'Comprehensive',
        description: 'Essential HR knowledge and legal compliance for managers and team leaders.',
        students: '7,450+',
        duration: '3 hours',
        icon: <CheckIcon data-id="element-3188" />,
        color: 'bg-teal'
      }],
      popularCategories: [{
        name: 'Leadership',
        count: '16 courses'
      }, {
        name: 'HR Management',
        count: '14 courses'
      }, {
        name: 'Team Building',
        count: '9 courses'
      }, {
        name: 'Workplace Culture',
        count: '12 courses'
      }],
      path: '/workplace'
    },
    business: {
      title: 'Business Skills',
      icon: <BriefcaseIcon size={20} className="text-teal" data-id="element-3189" />,
      courses: [{
        name: 'Cybersecurity',
        duration: '2 hours'
      }, {
        name: 'GDPR for Business',
        duration: '2 hours'
      }, {
        name: 'Marketing',
        duration: '3 hours'
      }, {
        name: 'Website Building',
        duration: '5 hours'
      }, {
        name: 'Google Ads',
        duration: '3 hours'
      }, {
        name: 'Meta Ads',
        duration: '3 hours'
      }, {
        name: 'TikTok Ads',
        duration: '2 hours'
      }],
      featuredCourses: [{
        title: 'Digital Marketing Masterclass',
        image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1115&q=80',
        badge: 'Bestseller',
        description: 'Master the fundamentals of digital marketing across multiple platforms.',
        students: '10,780+',
        duration: '5 hours',
        icon: <TrendingUpIcon data-id="element-3190" />,
        color: 'bg-rose-500'
      }, {
        title: 'Cybersecurity Fundamentals',
        image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
        badge: 'Critical',
        description: 'Protect your business with essential cybersecurity knowledge and practices.',
        students: '8,450+',
        duration: '2 hours',
        icon: <ShieldIcon data-id="element-3191" />,
        color: 'bg-cyan-500'
      }, {
        title: 'Social Media Strategy',
        image: 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1074&q=80',
        badge: 'New',
        description: 'Develop effective social media strategies to grow your brand and engage customers.',
        students: '7,320+',
        duration: '3 hours',
        icon: <BarChartIcon data-id="element-3192" />,
        color: 'bg-orange-500'
      }],
      popularCategories: [{
        name: 'Digital Marketing',
        count: '22 courses'
      }, {
        name: 'IT Security',
        count: '14 courses'
      }, {
        name: 'Business Analytics',
        count: '11 courses'
      }, {
        name: 'Social Media',
        count: '17 courses'
      }],
      path: '/business'
    }
  };
  const activeData = tabs[activeTab];
  return <section ref={sectionRef} className="py-24 bg-gray-50 opacity-0 transition-opacity duration-1000" data-id="element-3193">
      <div className="container mx-auto px-4 md:px-6" data-id="element-3194">
        <div className="max-w-4xl mx-auto mb-16 text-center" data-id="element-3195">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-charcoal tracking-tight" data-id="element-3196">
            Training for Every Industry
          </h2>
          <p className="text-xl text-gray-600" data-id="element-3197">
            From compliance to professional growth — tailored training for
            individuals, teams, and enterprises
          </p>
        </div>
        {/* Tabs */}
        <div className="flex flex-wrap justify-center mb-16 border-b border-gray-200" data-id="element-3198">
          {Object.entries(tabs).map(([key, value]) => <button key={key} onClick={() => handleTabChange(key)} className={`flex items-center px-8 py-5 font-medium text-lg transition-all duration-300 relative ${activeTab === key ? 'text-charcoal font-bold' : 'text-gray-500 hover:text-charcoal'}`} data-id="element-3199">
              <span className="mr-2" data-id="element-3200">{value.icon}</span>
              <span data-id="element-3201">{value.title}</span>
              {activeTab === key && <span className="absolute bottom-0 left-0 w-full h-1 bg-teal" data-id="element-3202"></span>}
            </button>)}
        </div>
        {/* Content */}
        <div className={`transition-opacity duration-300 ${isAnimating ? 'opacity-0' : 'opacity-100'}`} data-id="element-3203">
          <div className="mb-10" data-id="element-3204">
            <div className="flex items-center justify-between mb-8" data-id="element-3205">
              <h3 className="text-2xl font-bold text-charcoal flex items-center" data-id="element-3206">
                <StarIcon className="h-6 w-6 mr-2 text-amber-500" data-id="element-3207" />
                Top {activeData.title} Courses
              </h3>
              <a href={`${activeData.path}/popular`} className="text-teal font-medium hover:text-aqua transition-colors flex items-center" data-id="element-3208">
                See all popular courses
                <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-3209" />
              </a>
            </div>
            {/* Featured courses in grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3210">
              {activeData.featuredCourses.map((course, index) => <div key={index} className="rounded-xl shadow-lg overflow-hidden border border-gray-100 transform transition-all duration-500 hover:-translate-y-2 hover:shadow-xl bg-white h-full flex flex-col" data-id="element-3211">
                  <div className="relative h-48" data-id="element-3212">
                    <img src={course.image} alt={course.title} className="w-full h-full object-cover" data-id="element-3213" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-3214"></div>
                    <div className="absolute top-4 right-4 px-4 py-2 text-sm font-medium bg-aqua/70 backdrop-blur-sm text-white rounded-full" data-id="element-3215">
                      {course.badge}
                    </div>
                    <div className="absolute bottom-4 left-4 flex items-center text-white" data-id="element-3216">
                      <div className={`h-10 w-10 rounded-full ${course.color} flex items-center justify-center mr-2`} data-id="element-3217">
                        {cloneElement(course.icon, {
                      size: 20,
                      className: 'text-white'
                    })}
                      </div>
                      <div className="flex items-center text-white/90 text-sm" data-id="element-3218">
                        <UsersRoundIcon className="h-3.5 w-3.5 mr-1" data-id="element-3219" />
                        <span data-id="element-3220">{course.students}</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-6 flex-grow flex flex-col" data-id="element-3221">
                    <h4 className="text-xl font-bold mb-2 text-charcoal" data-id="element-3222">
                      {course.title}
                    </h4>
                    <p className="text-gray-600 mb-4 flex-grow" data-id="element-3223">
                      {course.description}
                    </p>
                    <div className="flex items-center justify-between mt-auto" data-id="element-3224">
                      <div className="flex items-center text-gray-500" data-id="element-3225">
                        <ClockIcon className="h-4 w-4 mr-1" data-id="element-3226" />
                        <span className="text-sm" data-id="element-3227">{course.duration}</span>
                      </div>
                      <a href={`/courses/${course.title.toLowerCase().replace(/\s+/g, '-')}`} className="text-teal font-medium hover:text-aqua transition-colors flex items-center" data-id="element-3228">
                        Learn more
                        <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-3229" />
                      </a>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mb-12" data-id="element-3230">
            {/* Course Categories */}
            <div className="lg:col-span-1" data-id="element-3231">
              <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100" data-id="element-3232">
                <h4 className="text-lg font-bold mb-6 text-charcoal flex items-center" data-id="element-3233">
                  <BriefcaseIcon className="h-5 w-5 mr-2 text-teal" data-id="element-3234" />
                  Popular {activeData.title} Categories
                </h4>
                <div className="space-y-4" data-id="element-3235">
                  {activeData.popularCategories.map((category, index) => <a key={index} href={`${activeData.path}/${category.name.toLowerCase().replace(/\s+/g, '-')}`} className="flex items-center justify-between p-4 border border-gray-100 rounded-lg hover:bg-gray-50 transition-all duration-300 group" data-id="element-3236">
                      <span className="font-medium text-charcoal group-hover:text-teal transition-colors" data-id="element-3237">
                        {category.name}
                      </span>
                      <span className="text-sm text-gray-500" data-id="element-3238">
                        {category.count}
                      </span>
                    </a>)}
                </div>
              </div>
            </div>
            {/* All courses section */}
            <div className="lg:col-span-2" data-id="element-3239">
              <div className="bg-white rounded-xl p-8 shadow-md border border-gray-100 h-full flex flex-col" data-id="element-3240">
                <div className="flex items-center mb-6" data-id="element-3241">
                  <AwardIcon className="h-10 w-10 text-teal mr-4" data-id="element-3242" />
                  <h3 className="text-2xl font-bold text-charcoal" data-id="element-3243">
                    Explore Our Complete Catalog
                  </h3>
                </div>
                <p className="text-gray-600 mb-8" data-id="element-3244">
                  Discover our full range of {activeData.title.toLowerCase()}{' '}
                  training solutions designed to meet regulatory requirements
                  and enhance your team's skills.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-id="element-3245">
                  <div className="flex items-start" data-id="element-3246">
                    <div className="w-8 h-8 rounded-full bg-teal/10 flex items-center justify-center mt-0.5 mr-3 flex-shrink-0" data-id="element-3247">
                      <CheckIcon className="h-4 w-4 text-teal" data-id="element-3248" />
                    </div>
                    <div data-id="element-3249">
                      <h5 className="font-medium text-charcoal" data-id="element-3250">
                        Accredited Certification
                      </h5>
                      <p className="text-sm text-gray-500" data-id="element-3251">
                        Recognized by regulatory bodies
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-3252">
                    <div className="w-8 h-8 rounded-full bg-teal/10 flex items-center justify-center mt-0.5 mr-3 flex-shrink-0" data-id="element-3253">
                      <CheckIcon className="h-4 w-4 text-teal" data-id="element-3254" />
                    </div>
                    <div data-id="element-3255">
                      <h5 className="font-medium text-charcoal" data-id="element-3256">
                        Interactive Learning
                      </h5>
                      <p className="text-sm text-gray-500" data-id="element-3257">
                        Engaging content and assessments
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-3258">
                    <div className="w-8 h-8 rounded-full bg-teal/10 flex items-center justify-center mt-0.5 mr-3 flex-shrink-0" data-id="element-3259">
                      <CheckIcon className="h-4 w-4 text-teal" data-id="element-3260" />
                    </div>
                    <div data-id="element-3261">
                      <h5 className="font-medium text-charcoal" data-id="element-3262">Self-Paced</h5>
                      <p className="text-sm text-gray-500" data-id="element-3263">
                        Learn on your own schedule
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-3264">
                    <div className="w-8 h-8 rounded-full bg-teal/10 flex items-center justify-center mt-0.5 mr-3 flex-shrink-0" data-id="element-3265">
                      <CheckIcon className="h-4 w-4 text-teal" data-id="element-3266" />
                    </div>
                    <div data-id="element-3267">
                      <h5 className="font-medium text-charcoal" data-id="element-3268">
                        Progress Tracking
                      </h5>
                      <p className="text-sm text-gray-500" data-id="element-3269">
                        Monitor completion and results
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-auto space-y-4" data-id="element-3270">
                  <a href={activeData.path} className="flex items-center justify-between w-full p-5 bg-gradient-to-r from-teal to-teal/90 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 text-white font-medium" data-id="element-3271">
                    <div className="flex items-center" data-id="element-3272">
                      <ArrowRightIcon className="h-5 w-5 mr-3" data-id="element-3273" />
                      <span data-id="element-3274">View All {activeData.title} Courses</span>
                    </div>
                    <div className="bg-white/20 p-2 rounded-full" data-id="element-3275">
                      <ArrowRightIcon className="h-5 w-5" data-id="element-3276" />
                    </div>
                  </a>
                  <a href="/courses" className="flex items-center justify-between w-full p-5 bg-white border-2 border-teal rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 text-teal font-medium" data-id="element-3277">
                    <div className="flex items-center" data-id="element-3278">
                      <BriefcaseIcon className="h-5 w-5 mr-3" data-id="element-3279" />
                      <span data-id="element-3280">Explore All Training Courses</span>
                    </div>
                    <div className="bg-teal/10 p-2 rounded-full" data-id="element-3281">
                      <ArrowRightIcon className="h-5 w-5" data-id="element-3282" />
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};